// src/features/tabs/PaneSurface.tsx
// How a page tells the pane it is in that it owns the whole surface.
//
// A pane pads its page and, when the group is set to "contained", centres it in
// a max-width column. That is right for lists and forms and wrong for pages
// that are an application in their own right — a chat with its own conversation
// column, a pipeline canvas, a secrets console. Those need the pane's whole
// rectangle and scroll their own regions inside it.
//
// The page declares it instead of the pane recognising it: a pane only knows a
// route, routes get renamed and moved between products, while "I fill my
// surface" is a fact about how the page is built.

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export interface PaneSurface {
  /** Registers one full-surface page; the returned function unregisters it. */
  claim: () => () => void;
}

const PaneSurfaceCtx = createContext<PaneSurface | null>(null);

/**
 * The pane's side of the contract: whether any mounted page is claiming the
 * whole surface, plus the handle to pass down to those pages.
 *
 * Counted rather than a boolean because during a navigation inside the pane the
 * outgoing and the incoming page are both mounted for a moment, and the
 * outgoing one must not release a surface the incoming one has just claimed.
 */
export function usePaneSurface(): { fullSurface: boolean; surface: PaneSurface } {
  const [claims, setClaims] = useState(0);

  const surface = useMemo<PaneSurface>(
    () => ({
      claim() {
        setClaims((n) => n + 1);
        return () => setClaims((n) => n - 1);
      },
    }),
    [],
  );

  return { fullSurface: claims > 0, surface };
}

export function PaneSurfaceProvider({
  surface,
  children,
}: {
  surface: PaneSurface;
  children: ReactNode;
}) {
  return <PaneSurfaceCtx.Provider value={surface}>{children}</PaneSurfaceCtx.Provider>;
}

/**
 * Declares that this page fills its pane — no padding, no width cap — and
 * returns the classes its root element needs to stand up in that space.
 *
 * Inside a pane the height comes from the pane, which is already a bounded box.
 * Outside one — a page mounted straight into the document, as in a test or a
 * preview — there is no measured ancestor to inherit from, so it falls back to
 * the viewport rather than collapsing to nothing.
 */
export function useFullPaneSurface(): string {
  const surface = useContext(PaneSurfaceCtx);
  useEffect(() => surface?.claim(), [surface]);
  return surface ? 'h-full min-h-0' : 'h-[100dvh] min-h-0';
}
